import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../api/authApi'; // ✅ Use apiService instead

// ==================== ASYNC THUNKS ====================

// ✅ Create Subscription - Using apiService
export const createSubscriptionAsync = createAsyncThunk(
  'subscription/createSubscription',
  async (data, { rejectWithValue }) => {
    try {
      // Use apiService with custom endpoint
      const response = await apiService.api.post('/tiffin/monthly/subscribe', data);
      console.log('✅ Subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Create subscription error:', error);
      let errorMessage = error.message || 'Failed to create subscription';
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Get Subscription List
export const getSubscriptionListAsync = createAsyncThunk(
  'subscription/getSubscriptionList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.api.get('/tiffin/monthly');
      console.log('✅ Subscription list:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get subscription list error:', error);
      let errorMessage = error.message || 'Failed to get subscription list';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Get Subscription Details
export const getSubscriptionDetailsAsync = createAsyncThunk(
  'subscription/getSubscriptionDetails',
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await apiService.api.get(`/tiffin/monthly/${subscriptionId}`);
      console.log('✅ Subscription details:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get subscription details error:', error);
      let errorMessage = error.message || 'Failed to get subscription details';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Meal Off
export const mealOffAsync = createAsyncThunk(
  'subscription/mealOff',
  async ({ subscriptionId, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.api.post(`/tiffin/monthly/${subscriptionId}/meal-off`, data);
      console.log('✅ Meal off set:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Meal off error:', error);
      let errorMessage = error.message || 'Failed to set meal off';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Cancel Subscription
export const cancelSubscriptionAsync = createAsyncThunk(
  'subscription/cancelSubscription',
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await apiService.api.post(`/tiffin/monthly/${subscriptionId}/cancel`, {});
      console.log('✅ Subscription cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Cancel subscription error:', error);
      let errorMessage = error.message || 'Failed to cancel subscription';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  subscriptions: [],
  currentSubscription: null,
  isLoading: false,
  error: null,
  success: false,
  lastUpdated: null,
};

// ==================== SLICE ====================

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearSubscription: (state) => {
      state.currentSubscription = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== CREATE SUBSCRIPTION ==========
      .addCase(createSubscriptionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSubscriptionAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload?.data || action.payload;
        state.success = true;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createSubscriptionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create subscription';
        state.success = false;
      })
      
      // ========== GET SUBSCRIPTION LIST ==========
      .addCase(getSubscriptionListAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionListAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptions = action.payload?.data || action.payload || [];
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getSubscriptionListAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to get subscription list';
      })
      
      // ========== GET SUBSCRIPTION DETAILS ==========
      .addCase(getSubscriptionDetailsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionDetailsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload?.data || action.payload;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getSubscriptionDetailsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to get subscription details';
      })
      
      // ========== MEAL OFF ==========
      .addCase(mealOffAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mealOffAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload?.data || action.payload;
        state.success = true;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(mealOffAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to set meal off';
      })
      
      // ========== CANCEL SUBSCRIPTION ==========
      .addCase(cancelSubscriptionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscriptionAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = null;
        state.success = true;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(cancelSubscriptionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to cancel subscription';
      });
  },
});

// ==================== EXPORT ACTIONS & REDUCER ====================

export const {
  clearError,
  clearSuccess,
  clearSubscription,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;